<template>
  <main-input-dialog
    ref="table-buttons-copy-objects-geometry-dialog"
    :is-visible.sync="computedIsVisible"
    :loading="computedIsLoading"
    :custom-is-valid="featuresToCopyValid || !requiredAttributesFormVisible"
    :title-translation="
      dialogProps.titleTranslation ? dialogProps.titleTranslation : $i18n.t('sidebar.copyObjectsGeometry.title')
    "
    :agree-text="dialogProps.agreeText ? dialogProps.agreeText : 'dialog.agreeCopy'"
    :dialog-width="requiredAttributesFormVisible ? '55%' : '30%'"
    @agree="copyObjectsGeometryHandler"
    :inputs="copyObjectsGeometryInputs"
    no-paddings-y
    no-paddings-x
    disable-default-form-bottom-padding
  >
    <template #layerId.input="data">
      <v-col cols="6">
        <data-input v-model="targetLayerId" v-bind="data" />
      </v-col>
    </template>
    <template #requiredAttributesForm.input>
      <v-row no-gutters>
        <v-col cols="12">
          <main-loader v-if="requiredAttributesFormLoading" />
          <template v-else>
            <v-row no-gutters class="border-top">
              <v-col cols="6" class="border-right pa-7">
                <v-row no-gutters>
                  <v-col cols="12">
                    <v-alert dense text icon="mdi-information-outline" color="primary" class="text-body-2 text-left">
                      {{ $i18n.t('sidebar.copyObjectsGeometry.fillRequiredAttributes') }}
                    </v-alert>
                  </v-col>
                </v-row>
                <ribbon-list :list-items="computedFeaturesToCopy" :toggleable="false" />
              </v-col>
              <v-col cols="6" class="pa-7">
                <template v-if="activeFeatureToCopy">
                  <v-row no-gutters align="center" v-for="(requiredAttribute, idx) of requiredAttributes" :key="idx">
                    <v-col cols="11" class="pr-2">
                      <data-input
                        v-bind="requiredAttribute"
                        v-model="activeFeatureToCopy.requiredValues[requiredAttribute.attribute]"
                        @input="checkValidation"
                        :readonly="computedIsLoading"
                      />
                    </v-col>
                    <v-col cols="auto" class="pb-5">
                      <v-tooltip bottom>
                        <template #activator="{on}">
                          <v-icon
                            size="18"
                            class="pb-1 pr-1"
                            v-on="on"
                            @click="
                              copyValueToOthers(
                                requiredAttribute.attribute,
                                activeFeatureToCopy.requiredValues[requiredAttribute.attribute]
                              )
                            "
                          >
                            mdi-content-copy
                          </v-icon>
                        </template>
                        {{ $i18n.t('sidebar.copyObjectsGeometry.copyValueToOthers') }}
                      </v-tooltip>
                    </v-col>
                  </v-row>
                </template>
              </v-col>
            </v-row>
          </template>
        </v-col>
      </v-row>
    </template>
  </main-input-dialog>
</template>
<script>
import { call, get, sync } from 'vuex-pathify';

import DataInput from '@/components/DataInput';
import MainInputDialog from '@/components/MainInputDialog';
import MainLoader from '@/components/MainLoader';
import RibbonList from '@/components/RibbonList';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';
import topo from '@/mixins/topo';

export default {
  name: 'TableButtonsCopyObjectsGeometryDialog',
  components: { DataInput, MainInputDialog, MainLoader, RibbonList },
  mixins: [prepareAttributesFeatureCardMixin, topo],
  props: {
    isLoading: {
      type: Boolean,
    },
    isVisible: {
      type: Boolean,
    },
    inputLayerId: {
      type: [Number, String],
    },
    availableLayersRules: {
      type: Object,
      default: () => ({}),
    },
    openFeatureCardEditor: {
      type: Boolean,
      default: false,
    },
    dialogProps: {
      type: Object,
      default: () => ({}),
    },
    copyLayerInputProps: {
      type: Object,
      default: () => ({}),
    },
    customGeometry: {
      type: Object,
      default: null,
    },
    inputFeaturesIds: {
      type: Array,
      default: () => {
        return [];
      },
    },
  },
  computed: {
    currentLayer: get('layers/currentLayer'),
    newFeatureGeometry: sync('edit/newFeatureGeometry'),
    computedIsVisible: {
      get() {
        if (!this.customGeometry && !this.inputFeaturesIds?.length) return false;
        return this.isVisible || false;
      },
      set(nV) {
        if (!this.inputFeaturesIds?.length) {
          this.$emit('update:isVisible', false);
          return;
        }
        this.$emit('update:isVisible', nV);
      },
    },
    computedIsLoading: {
      get() {
        return this.isLoading || false;
      },
      set(nV) {
        this.$emit('update:isLoading', nV);
      },
    },
    layers: get('layers/layers'),
    dataSources: get('layers/metadata'),
    inputLayer() {
      return this.layers?.[this.inputLayerId] || undefined;
    },
    inputLayerVerboseName() {
      return this.inputLayer?.name || '';
    },
    inputLayerAttributesSchema() {
      return this.inputLayer?.form_schema?.elements || [];
    },
    inputLayerAttributes() {
      return this.$_flattenStructure(this.getForm(this.inputLayerAttributesSchema, this.inputDataSourceAttributes));
    },
    inputDataSourceAttributes() {
      return this.inputDataSource?.attributes_schema?.attributes || [];
    },
    inputLayerDescAttributeVerboseName() {
      return (
        this.inputLayerAttributes.find(attribute => attribute.attribute === this.inputDataSourceDescAttributeName)
          ?.label || ''
      );
    },
    inputDataSourceName() {
      return this.inputLayer?.data_source_name || '';
    },
    inputDataSource() {
      return this.dataSources?.[this.inputDataSourceName] || undefined;
    },
    inputDataSourceIdAttributeName() {
      return this.inputDataSource?.attributes_schema?.id_name || '';
    },
    inputDataSourceIdAttributeFilterName() {
      return this.inputDataSourceUseQualifiedNames
        ? this.inputDataSourceIdAttributeName
        : `${this.inputDataSourceName}.${this.inputDataSourceIdAttributeName}`;
    },
    inputDataSourceUseQualifiedNames() {
      return this.inputDataSource?.attributes_use_datasource_qualified_names || false;
    },
    inputDataSourceDescAttributeName() {
      return this.inputDataSource?.attributes_schema?.desc_attribute_name || '';
    },
    targetLayer() {
      return this.layers?.[this.targetLayerId] || undefined;
    },
    targetLayerTopo() {
      return this.isTopologicalLayer(this.targetLayer);
    },
    targetLayerAttributesSchema() {
      return this.targetLayer?.form_schema?.elements || [];
    },
    targetDataSourceName() {
      return this.targetLayer?.data_source_name || '';
    },
    targetDataSource() {
      return this.dataSources?.[this.targetDataSourceName] || undefined;
    },
    targetDataSourceAttributes() {
      return this.targetDataSource?.attributes_schema?.attributes || [];
    },
    targetLayerAttributes() {
      return this.$_flattenStructure(this.getForm(this.targetLayerAttributesSchema, this.targetDataSourceAttributes));
    },
    copyObjectsGeometryInputs() {
      return [
        {
          name: 'layerId',
          translationPath: 'sidebar.copyObjectsGeometry.targetLayer',
          subtitle: 'sidebar.copyObjectsGeometry.targetLayerSubtitle',
          dataType: {
            name: 'select',
          },
          items: this.targetLayers,
          itemValue: 'id',
          itemText: 'name',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          noDataText: this.$i18n.t('sidebar.copyObjectsGeometry.noLayersMatchingGeometryInProject'),
          inputClasses: ['pa-5'],
          readonly: this.computedIsLoading,
          ...this.copyLayerInputProps,
        },
        ...(this.requiredAttributesFormVisible
          ? [
              {
                name: 'requiredAttributesForm',
                leftCol: 0,
                dataType: {
                  name: 'text',
                },
              },
            ]
          : []),
      ];
    },
    targetLayers() {
      const { geometry_type: currentLayerGeometry = '', id: currentLayerId = null } = this.inputLayer || {};
      if (!this.customGeometry && ((!currentLayerId && currentLayerId !== 0) || !currentLayerGeometry)) return [];
      const layerGeometryMap = {
        point: 'point',
        multipoint: 'point',
        linestring: 'linestring',
        multilinestring: 'linestring',
        polygon: 'polygon',
        multipolygon: 'polygon',
      };
      return (this.projectLayers || [])
        .filter(
          layer =>
            !layer.cache &&
            (this.availableLayersRules?.geometry_type || this.availableLayersRules?.layer_scope
              ? !!layer.geometry_type &&
                (this.availableLayersRules.layer_scope
                  ? layer.layer_scope === this.availableLayersRules.layer_scope
                  : true) &&
                (this.availableLayersRules.geometry_type
                  ? this.availableLayersRules.geometry_type.includes(layer.geometry_type)
                  : true)
              : layer.id != currentLayerId &&
                !!layer.geometry_type &&
                layer.geometry_type.includes(layerGeometryMap[currentLayerGeometry]))
        )
        .map(layer => {
          const { id, name } = layer;
          return { id, name };
        });
    },
    activeFeatureToCopy() {
      return this.featuresToCopy.find(feature => feature.id === this.activeFeatureToCopyId);
    },
    computedFeaturesToCopy() {
      return this.featuresToCopy.map(feature => {
        return {
          ...feature,
          itemClickHandler: this.toggleFeatureToCopy,
          classes: ['py-1', ...(feature.id === this.activeFeatureToCopyId ? ['list-element-active'] : [])],
        };
      });
    },
    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
  },
  data: () => ({
    activeFeatureToCopyId: null,
    activeFeatureToCopyIdx: null,
    featuresToCopy: [],
    featuresToCopyValid: false,
    inputFeaturesDescsValues: {},
    requiredAttributes: [],
    requiredAttributesFormLoading: false,
    requiredAttributesFormVisible: false,
    targetLayerId: null,
  }),
  methods: {
    addTopoFeatureAction: call('layers/addTopoDatasourceFeature'),
    getLayerFeatures: call('layers/getLayerFeatures'),
    uploadDataSource: call('layers/uploadDataSource'),
    checkValidation() {
      this.featuresToCopyValid = this.featuresToCopy.every(feature => {
        return Object.values(feature.requiredValues).every(value => !!value || value === 0 || value === false);
      });
    },
    copyValueToOthers(attribute, value) {
      this.featuresToCopy = this.featuresToCopy.map(feature => {
        return { ...feature, requiredValues: { ...feature.requiredValues, [attribute]: value } };
      });
      this.$nextTick(() => {
        this.checkValidation();
      });
    },
    toggleFeatureToCopy(item) {
      if (this.computedIsLoading) return;
      this.activeFeatureToCopyId = item?.id || item?.id === 0 ? item.id : null;
      this.$refs['table-buttons-copy-objects-geometry-dialog']?.$refs['form']?.resetValidation();
    },
    async copyObjectsGeometryHandler() {
      this.computedIsLoading = true;
      try {
        let geojson = JSON.parse(JSON.stringify(this.customGeometry));
        if (!geojson && this.inputFeaturesIds?.length > 0) {
          const payload = {
            layer_id: this.inputLayerId,
            features_filter: {
              $IN: {
                [this.inputDataSourceIdAttributeFilterName]: this.inputFeaturesIds,
              },
            },
          };
          const r = await this.getLayerFeatures(payload);
          geojson = r.data.data.features;
          geojson.features = geojson.features.map(feature => {
            feature.properties =
              this.featuresToCopy.find(featureToCopy => featureToCopy.id === feature.id)?.requiredValues || {};
            if (!this.targetLayerTopo) delete feature.id;
            return feature;
          });
        }
        if (this.openFeatureCardEditor) {
          // This method takes first feature's geometry from collection.
          // Change it via props if needed or pass valid single geometry.
          this.newFeatureGeometry =
            geojson.type === 'FeatureCollection'
              ? {
                  ...geojson.features[0].geometry,
                  crs: geojson.crs,
                }
              : geojson;
          this.$router.push({
            name: 'layerFeature',
            params: {
              ...this.$route.params,
              lid: this.targetLayerId,
              fid: 'new',
            },
          });
          this.computedIsVisible = false;
        } else {
          await this[`copy${this.targetLayerTopo ? '' : 'Non'}TopoObjectsGeometry`](geojson);
        }
      } finally {
        this.computedIsLoading = false;
      }
    },
    async copyNonTopoObjectsGeometry(geojson) {
      try {
        const blob = new Blob([JSON.stringify(geojson)], { type: 'application/geo+json;charset=utf-8;' });
        const file = new File([blob], 'copyFeatures.geojson');
        const formData = new FormData();
        formData.append('geojson', file);
        formData.append('srid', geojson.crs.properties.name.split(':')[1]);
        formData.append('datasource', this.targetDataSourceName);
        await this.uploadDataSource(formData);
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('sidebar.copyObjectsGeometry.successCopyFeatures'),
        });
        if (this.currentLayer?.id === this.targetLayerId) {
          this.$root.$emit('refreshTableData');
        }
        this.projectLayers.forEach(layer => {
          if (layer.data_source_name === this.targetDataSourceName) {
            this.$root.$emit('refreshMvtSource', layer.id);
          }
        });
        this.computedIsLoading = false;
        this.computedIsVisible = false;
      } catch {
        this.computedIsLoading = false;
      }
    },
    async copyTopoObjectsGeometry(geojson) {
      const { crs, features } = geojson;
      const errors = [];
      for await (const feature of features) {
        try {
          const { geometry, properties } = feature;
          const payload = {
            datasource: this.targetDataSourceName,
            body: { properties, geometry: { ...geometry, crs } },
            params: { layer_id: this.targetLayerId },
            skipDefaultErrorHandler: true,
          };
          await this.addTopoFeatureAction(payload);
        } catch (error) {
          const { id: featureId } = feature;
          const descValue = this.inputFeaturesDescsValues[featureId]?.desc || '';
          const title = `${this.inputLayerVerboseName}: ${this.inputLayerDescAttributeVerboseName} ${descValue}`;
          errors.push({ featureId, error: error.message, title });
        }
      }
      if (errors?.length) {
        this.$emit('topoCopyErrors', errors);
      } else {
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('sidebar.copyObjectsGeometry.successCopyFeatures'),
        });
        this.projectLayers.forEach(layer => {
          if (layer.data_source_name === this.targetDataSourceName) {
            this.$root.$emit('refreshMvtSource', layer.id);
          }
        });
        this.computedIsLoading = false;
        this.computedIsVisible = false;
      }
    },
    async getRequiredAttributesForm() {
      const requiredAttributes = this.targetLayerAttributes.filter(
        attribute => attribute.required && attribute.editable
      );
      const requiredValues = this.getValues(requiredAttributes, {});
      if (requiredAttributes?.length) {
        this.requiredAttributesFormVisible = true;
        this.requiredAttributesFormLoading = true;
        const payload = {
          layer_id: this.inputLayerId,
          params: {
            ids_descs_only: true,
          },
          features_filter: {
            $IN: {
              [this.inputDataSourceIdAttributeFilterName]: this.inputFeaturesIds,
            },
          },
        };
        const r = await this.getLayerFeatures(payload);
        this.inputFeaturesDescsValues = Object.freeze(this.$_arrayToObject(r.data.data?.ids || []));
        this.featuresToCopy = this.inputFeaturesIds.map(featureId => {
          return {
            id: featureId,
            title: `${this.inputLayerDescAttributeVerboseName}: ${this.inputFeaturesDescsValues[featureId]?.desc ||
              ''}`,
            requiredValues: JSON.parse(JSON.stringify(requiredValues)),
          };
        });
        this.requiredAttributes = requiredAttributes;
        this.activeFeatureToCopyId = null;
        this.requiredAttributesFormLoading = false;
        this.$nextTick(() => {
          this.checkValidation();
        });
      } else {
        this.requiredAttributesFormVisible = false;
        this.featuresToCopy = this.inputFeaturesIds.map(featureId => {
          return { id: featureId, requiredValues };
        });
        this.requiredAttributes = [];
        this.activeFeatureToCopyId = null;
        this.requiredAttributesFormLoading = false;
      }
    },
  },
  watch: {
    computedIsVisible: {
      handler(nV) {
        if (nV) return;
        this.computedIsLoading = false;
        this.featuresToCopy = [];
        this.requiredAttributes = [];
        this.activeFeatureToCopyId = null;
        this.targetLayerId = null;
      },
    },
    targetLayerId: {
      handler(nV) {
        if (this.openFeatureCardEditor) return;
        this.requiredAttributesFormVisible = false;
        this.requiredAttributesFormLoading = false;
        if (!nV && nV !== 0) {
          this.featuresToCopy = [];
          this.requiredAttributes = [];
          this.activeFeatureToCopyId = null;
          return;
        }
        this.getRequiredAttributesForm();
      },
      immediate: true,
    },
  },
};
</script>
<style lang="scss" scoped>
.border-top {
  @include border(top, 1px);
}
.border-right {
  @include border(right, 1px);
}
</style>
